import { Button, Grid, Heading } from '@raskrask-dk/rr-components'
import { ReactElement } from 'react'
import { PageTemplate } from '../chemistry/skeleton'
import { DetailsFields, PropsDetailsTemplate } from './props'
import { useTranslation } from 'react-i18next'
import { getIsMobile } from '../../redux/reducers/appSettings'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { HelpField, StyledDetails } from './styles'
import CustomerFieldsTemplate from './FieldGroups/CustomerFields'
import GoToPaymentTemplate from './Fields/GoToPayment'
import TermsTemplate from './Fields/Terms'
import AdditionalFieldsTemplate from './FieldGroups/AdditionalFields'
import { ButtonWrapper } from './Fields/GoToPayment/styles'
import { getShowHelp, setShowHelp } from '../../redux/reducers/detailsSettings'
import CompanyLeadSummaryTemplate from './Fields/companyLeadSummary'
import { PartnerBF } from '../../models'
import { CodeValidator } from '../chemistry/detailsComponents/Codes/CodeValidator'
import { ReturningCustomerDiscount } from '../chemistry/detailsComponents/returningCustomerDiscount'

const DetailsTemplate = ({
    fieldArray,
    basisType,
    partner,
    errorHandler,
    paymentButtonHandler,
    companyNameHandler,
    contactNameHandler,
    emailHandler,
    nameHandler,
    phoneHandler,
    setIsAccepted,
    isAccepted,
    isCompanyBooking,
    isCompanyLead,
    onClickBack,
    customerType,
    bookingReasonHandler,
    descriptionHandler,
    parkingHandler,
    specialCareHandler,
    companySettings,
    codesProps,
    goalId,
    returningCustomerProps,
}: PropsDetailsTemplate): ReactElement => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const isMobile = useAppSelector(getIsMobile)
    const showHelp = useAppSelector(getShowHelp)

    const [submitClickedWithMissingDetails] = errorHandler

    const [canGoToPayment, price, onClickGoToPayment] = paymentButtonHandler

    const shouldRender = (inputFieldName: DetailsFields): boolean => {
        const element = fieldArray.find((element) => element === inputFieldName)
        if (element) return true
        return false
    }

    const helpFieldButton = (
        <ButtonWrapper>
            <Button
                data-cy="descriptionCTAMobile"
                onClick={() => dispatch(setShowHelp(false))}
                size="large"
                variant="success"
            >
                {t('common:continue')}
            </Button>
        </ButtonWrapper>
    )

    const ctaButtonText = isCompanyLead
        ? t('details.company.paybtn')
        : t('details.paybtn')

    const Top = (
        <Heading bold primary size={isMobile ? 5 : 4}>
            {t('details.title')}
        </Heading>
    )

    const shouldRenderAdditionalFields = shouldRender('additionalFields')

    const Body = (
        <StyledDetails>
            <Grid
                autoFlow={isMobile ? 'row' : 'column'}
                css={{
                    alignItems: 'start',
                    justifyContent: 'center',
                    '@desktop': { gridAutoColumns: 'minmax(0, 450px)' },
                }}
                gap={isMobile ? 16 : 32}
            >
                {isMobile && Top}
                {showHelp && isCompanyLead && (
                    <>
                        <CompanyLeadSummaryTemplate
                            companySettings={companySettings}
                        />
                        {isMobile && helpFieldButton}
                    </>
                )}
                {(showHelp || !isMobile || (isCompanyBooking && showHelp)) &&
                    shouldRenderAdditionalFields && (
                        <>
                            <HelpField
                                variant={
                                    isCompanyBooking ? 'OneOff' : 'Default'
                                }
                            >
                                <Grid gap={16}>
                                    <AdditionalFieldsTemplate
                                        customerType={customerType}
                                        bookingReasonHandler={[
                                            ...bookingReasonHandler,
                                        ]}
                                        descriptionHandler={[
                                            ...descriptionHandler,
                                        ]}
                                        parkingHandler={[...parkingHandler]}
                                        specialCareHandler={[
                                            ...specialCareHandler,
                                        ]}
                                        goalId={goalId}
                                    />
                                </Grid>
                            </HelpField>
                            {isMobile && helpFieldButton}
                        </>
                    )}
                {(!showHelp ||
                    !isMobile ||
                    (!isCompanyLead && !shouldRenderAdditionalFields)) && (
                    <Grid gap={16}>
                        <CustomerFieldsTemplate
                            companyNameHandler={[
                                ...companyNameHandler,
                                shouldRender('companyName'),
                            ]}
                            contactNameHandler={[
                                ...contactNameHandler,
                                shouldRender('contactName'),
                            ]}
                            emailHandler={[
                                ...emailHandler,
                                shouldRender('customerEmail'),
                            ]}
                            nameHandler={[
                                ...nameHandler,
                                shouldRender('customerName'),
                            ]}
                            phoneHandler={[
                                ...phoneHandler,
                                shouldRender('customerPhone'),
                            ]}
                            customerType={customerType}
                            submitClickedWithMissingDetails={
                                submitClickedWithMissingDetails
                            }
                        />
                        {shouldRender('returningCustomerDiscount') && (
                            <ReturningCustomerDiscount
                                {...returningCustomerProps}
                            />
                        )}
                        {shouldRender('codes') && (
                            <CodeValidator {...codesProps} />
                        )}
                        {shouldRender('terms') && (
                            <TermsTemplate
                                partner={partner as PartnerBF}
                                basisType={basisType}
                                isAccepted={isAccepted}
                                setIsAccepted={setIsAccepted}
                                submitClickedWithMissingDetails={
                                    submitClickedWithMissingDetails
                                }
                                isCompanyBooking={isCompanyBooking}
                            />
                        )}
                        <GoToPaymentTemplate
                            buttonText={ctaButtonText}
                            canGoToPayment={canGoToPayment}
                            onClickGoToPayment={onClickGoToPayment}
                        />
                    </Grid>
                )}
            </Grid>
        </StyledDetails>
    )

    return (
        <PageTemplate
            Top={!isMobile && Top}
            Body={Body}
            Modal={undefined}
            bodyOverlap={!isMobile}
            onClickBack={onClickBack}
        />
    )
}

export default DetailsTemplate
